import { isAxiosError } from "axios";
import type { AxiosError } from "axios";
import {
  AUTH_LOGIN_REQUIRED_MESSAGE,
  AUTH_SESSION_EXPIRED_MESSAGE,
} from "../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { ApiErrorOptions } from "@model/GlobalModels.model";

const GLOBAL_SERVER_ERROR_MESSAGE =
  "Could not connect to server. Please try again later or contact support.";

export const handleDefaultApiErrors = (
  error: unknown,
  opts?: ApiErrorOptions
): string => {
  let errorMessage =
    "A technical error occurred! Please try again later or contact support!";

  if (!isAxiosError(error)) {
    console.error("Non-Axios error:", error);
    return errorMessage;
  }

  const axiosError = error as AxiosError<any>;
  const response = axiosError.response;
  const status = response?.status;
  const data = response?.data;
  const code = axiosError.code;

  // ─────────────────────────────────────────────
  // 1) No response at all → never reached server
  // ─────────────────────────────────────────────
  if (!response) {
    if (code === "ECONNABORTED") {
      return "The request took too long. Please check your connection and try again.";
    }

    return GLOBAL_SERVER_ERROR_MESSAGE;
  }

  const backendMsg = (data as any)?.error ?? (data as any)?.message;

  //  401 auth error, use useForceLogoutAndRedirect
  if (status === 401 && opts?.onAuthError) {
    const hadToken = Boolean(opts.hadToken);
    const message = hadToken
      ? AUTH_SESSION_EXPIRED_MESSAGE
      : AUTH_LOGIN_REQUIRED_MESSAGE;

    try {
      opts.onAuthError(message);
    } catch (err) {
      console.error("Failed to handle auth error via onAuthError", err);
    }

    return backendMsg || message;
  }

  // 403  permission error
  if (status === 403 && opts?.onForbidden) {
    const message =
      backendMsg || "You are not allowed to perform this action.";

    try {
      opts.onForbidden(message);
    } catch (err) {
      console.error("Failed to handle forbidden error via onForbidden", err);
    }

    return message;
  }

  switch (status) {
    case 400: {
      errorMessage = handleObjectApiErrors(data as Record<string, string[]>);
      break;
    }
    case 404:
    case 500:
    case 502:
    case 503:
    case 504: {
      errorMessage = GLOBAL_SERVER_ERROR_MESSAGE;
      break;
    }
    case 408: {
      errorMessage =
        "Request timed out. Please check your network and try again.";
      break;
    }
    case 422: {
      errorMessage = handleArrayApiError(data);
      break;
    }
    default: {
      console.warn("API failure:", data);
      errorMessage =
        "A technical error occurred! Please try again later or contact support!";
      break;
    }
  }

  return backendMsg || errorMessage;
};

export const handleArrayApiError = (responseData: string[]): string => {
  return responseData.join("\n");
};

export const handleObjectApiErrors = (
  errorData: Record<string, string[]>,
): string => {
  const allMessages = Object.values(errorData)
    .flat()
    .filter(Boolean)
    .join("\n");

  return allMessages || "An unknown error occurred.";
};
